/**
 * A callback/subscriber for websocket lifecycle events.
 */
export type LifecycleEvent =
  | 'opened'
  | 'connecting'
  | 'failedToReconnect'
  | 'closed';

export type LifecycleListener = (lifecycle: LifecycleEvent) => void;

const listeners = new Set<LifecycleListener>();

/**
 * Subscribe to websocket lifecycle events.
 * Returns a callback which, when called, removes/unsubscribes that listener.
 *
 * The lifecycle events are:
 * - "`opened`": the connection has opened (under any circumstances).
 * - "`connecting`": we have begun attempting to open a connection. This may be because:
 *     (a) it's the first time a message is being sent;
 *     (b) something has requested a "reset" of SocketAsFetch (i.e, via `recreateSockets()`); or
 *     (c) the connection closed unexpectedly, and an automatic reconnection attempt is upcoming.
 * - "`failedToReconnect`": the connection was unexpectedly closed, but we are out of reconnection attempts.
 * - "`closed`": the connection has closed under normal circumstances without the intent of reopening it.
 */
export const subscribeToLifecycle = (listener: LifecycleListener) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

export const callLifecycleListeners = (lifecycle: LifecycleEvent) => {
  listeners.forEach((listener) => {
    try {
      listener(lifecycle);
    } catch (error) {
      /**
       * We catch errors here to ensure that one bad listener doesn't prevent other listeners from being called.
       * Any error handling concerns related to errors occurring within callbacks should be handled within the listeners themselves.
       */
    }
  });
};
